import { ReactNode, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BASKET_RETRIEVAL_TOKEN } from '@constants/queryParams';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useLocalSessionAddressContext } from '@providers/basket/LocalSessionAddressProvider/Context';
import { clearFormPersistStorage } from '@utils/storagePropertyManagement';

import { VerifyModal } from '../VerifyModal';
import { useVerifyModal } from '../hooks';

interface SessionWrapperProps {
  children: ReactNode;
}

const SessionWrapper = ({ children }: SessionWrapperProps) => {
  const {
    isLoading,
    postcodeError,
    handleClose,
    handleBasketRetrieval,
    resetPostcodeError,
  } = useVerifyModal();
  const [searchParams] = useSearchParams();
  const { resetAddressStorage } = useLocalSessionAddressContext();
  const { handleStartAgain: resetBasketStorage } = useBasketContext();

  // track the search param directly, as using the token stored in state will
  // cause issues due to the react event loop persisting the token before the
  // basket is successfully restored, therefore resetting prior to retrieving
  // the basket.
  const token = searchParams.get(BASKET_RETRIEVAL_TOKEN);

  useEffect(() => {
    // when a retrieval token is present in query params, reset the session and
    // remove any saved address data. It's assumed that entering via a retrieval
    // link means they are replacing any existing session.
    if (token) {
      resetBasketStorage();
      resetAddressStorage();
      clearFormPersistStorage();
    }
  }, [resetAddressStorage, resetBasketStorage, token]);

  return (
    <>
      {children}
      <VerifyModal
        error={postcodeError}
        isLoading={isLoading}
        onClose={handleClose}
        onSubmit={handleBasketRetrieval}
        resetError={resetPostcodeError}
      />
    </>
  );
};

export { SessionWrapper };
